<template>
  <div class="flex flex-col gap-4">
    <div class="flex items-center justify-between">
      <!--tabbar-->
      <slot />
      <!--search panel-->
      <div class="flex items-center gap-2">
        <div class="relative">
          <el-input
            placeholder="Search..."
            prefix-icon="el-icon-search"
            class="w-[250px]"
            clearable
            v-model="searchWords"
          />
        </div>
        <el-button @click="handleSchedule()" type="primary" icon="el-icon-plus">
          Add Schedule
        </el-button>
        <el-popover
          placement="bottom"
          trigger="click"
          width="320"
          v-model="filterPanelVisible"
        >
          <button
            slot="reference"
            class="ring-offset-background focus-visible:outline-hidden focus-visible:ring-ring inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&amp;_svg]:pointer-events-none [&amp;_svg]:size-4 [&amp;_svg]:shrink-0 border-input bg-background hover:bg-accent hover:text-accent-foreground border h-10 w-10"
            type="button"
          >
            <i class="icon-funnel h-4 w-4" />
          </button>
          <ETagFilterPanel @apply="handleApply" />
        </el-popover>
      </div>
    </div>
    <div
      class="flex-1 flex flex-col rounded-lg border bg-card text-card-foreground shadow-2xs"
    >
      <div class="flex flex-col space-y-1.5 p-6">
        <h3 class="text-2xl font-semibold leading-none tracking-tight">
          Price Update Schedules
        </h3>
        <p class="text-sm text-muted-foreground">
          Manage scheduled price updates for all displays.
        </p>
      </div>
      <div class="flex-1 flex flex-col relative overflow-hidden">
        <el-scrollbar class="absolute inset-0 scrollbar">
          <div class="p-6">
            <table class="w-full caption-bottom text-sm">
              <thead style="position: sticky; top: 0">
                <tr
                  class="border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted"
                >
                  <th
                    class="h-12 px-4 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0"
                  >
                    Schedule Name
                  </th>
                  <th
                    class="h-12 px-4 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0"
                  >
                    Product Name
                  </th>
                  <th
                    class="h-12 px-4 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0"
                  >
                    Price Change
                  </th>
                  <th
                    class="h-12 px-4 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0"
                  >
                    Start Date
                  </th>
                  <th
                    class="h-12 px-4 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0"
                  >
                    End Date
                  </th>
                  <th
                    class="h-12 px-4 align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0 text-right"
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                <template v-if="isLoading">
                  <tr v-for="n in 5" :key="'skeleton-' + n">
                    <td class="p-4 align-middle">
                      <div
                        class="h-5 w-24 bg-gray-200 rounded animate-pulse"
                      ></div>
                    </td>
                    <td class="p-4 align-middle">
                      <div
                        class="h-5 w-20 bg-gray-200 rounded animate-pulse"
                      ></div>
                    </td>
                    <td class="p-4 align-middle">
                      <div
                        class="h-5 w-28 bg-gray-200 rounded animate-pulse"
                      ></div>
                    </td>
                    <td class="p-4 align-middle">
                      <div
                        class="h-5 w-20 bg-gray-200 rounded animate-pulse"
                      ></div>
                    </td>
                    <td class="p-4 align-middle">
                      <div
                        class="h-5 w-20 bg-gray-200 rounded animate-pulse"
                      ></div>
                    </td>
                    <td class="p-4 align-middle text-right">
                      <div
                        class="h-5 w-16 bg-gray-200 rounded animate-pulse ml-auto"
                      ></div>
                    </td>
                  </tr>
                </template>
                <tr v-else-if="tableCtrl?.state.tableData.length === 0">
                  <td
                    class="p-4 align-middle text-center text-muted-foreground"
                    :colspan="7"
                    style="height: 400px"
                  >
                    <div
                      class="flex items-center justify-center h-full"
                      style="min-height: 200px"
                    >
                      No schedules found.
                    </div>
                  </td>
                </tr>
                <tr
                  v-else
                  v-for="item in tableCtrl?.state.tableData"
                  :key="item.id"
                  class="border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted"
                >
                  <td class="p-4 align-middle font-medium">
                    {{ item.name || "--" }}
                  </td>
                  <td class="p-4 align-middle">
                    {{
                      item.items.map((i) => i.product_code).join(", ") || "--"
                    }}
                  </td>
                  <td class="p-4 align-middle">
                    <div class="flex items-center gap-2">
                      <span
                        class="line-through text-muted-foreground"
                        style="text-decoration: line-through"
                      >
                        ${{
                          item.items
                            .map((i) => i.start_action.product_price)
                            .join(", ")
                        }}
                      </span>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        class="lucide lucide-arrow-up-down h-3 w-3 text-muted-foreground"
                      >
                        <path d="m21 16-4 4-4-4"></path>
                        <path d="M17 20V4"></path>
                        <path d="m3 8 4-4 4 4"></path>
                        <path d="M7 4v16"></path>
                      </svg>
                      <span class="font-medium"
                        >${{
                          item.items
                            .map((i) => i.end_action.product_price)
                            .join(", ") || "--"
                        }}</span
                      >
                    </div>
                  </td>
                  <td class="p-4 align-middle">
                    {{ item.start_time ? item.start_time.split("T")[0] : "--" }}
                  </td>
                  <td class="p-4 align-middle">
                    {{
                      item.end_time ? item.end_time.split("T")[0] : "Indefinite"
                    }}
                  </td>
                  <td class="p-4 align-middle text-right">
                    <div class="flex justify-end gap-4">
                      <i
                        class="fa fa-edit text-xl cursor-pointer"
                        style="color: #fcb900"
                        @click="handleSchedule(item)"
                      />
                      <i
                        class="fa fa-trash text-xl text-red-500 cursor-pointer"
                        @click="handleDeleteSchedule(item.id)"
                      />
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </el-scrollbar>
      </div>
      <div class="items-center p-6 pt-0 flex justify-between">
        <div class="flex flex-row">
          <el-button icon="fa fa-download"> Export Schedule </el-button>
        </div>
        <el-pagination
          @size-change="(val) => tableCtrl.setPageSize(val)"
          @current-change="(val) => tableCtrl.setCurrentPage(val)"
          :current-page="tableCtrl.state.pagination.page"
          :page-sizes="[10, 20, 50, 100]"
          :page-size="tableCtrl.state.pagination.pageSize"
          layout="total, sizes, prev, pager, next, jumper"
          :total="tableCtrl.state.pagination.total"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { TableController } from "@/controllers/TableController";
import { listPriceTasks, deletePriceTasks } from "@/api";
import ETagFilterPanel from "./ETagFilterPanel.vue";

export default {
  name: "SchedulesBoard",
  props: {
    searchText: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      tableCtrl: null,
      filterPanelVisible: false,
      searchWords:this.searchText
    };
  },
  created() {
    this.initTableController();
  },
  watch: {
    searchText() {
      if (!this.tableCtrl) return;
      this.tableCtrl.setQueryParameters({ name: this.searchText });
    },
  },
  computed: {
    isLoading() {
      return this.tableCtrl?.state.loading ?? false;
    },
  },
  methods: {
    initTableController() {
      this.tableCtrl = new TableController({
        loadMethod: listPriceTasks,
        pageSize: 10,
        isClientPaged: true,
        keyField: "id",
        listField: "taskList",
        queryParameters: {},
      });
    },
    async handleDeleteSchedule(id) {
      await this.$confirm(
        "Are you sure you want to delete this schedule?",
        "Confirm Deletion",
        {
          confirmButtonText: "Delete",
          cancelButtonText: "Cancel",
          type: "warning",
        }
      );
      try {
        await deletePriceTasks([id]);
      } catch (e) {
        this.$message.error("Failed to delete schedule: " + e.message);
        return;
      }
      this.$message.success("Schedule deleted successfully");
      this.tableCtrl.loadTableData();
    },
    async handleSchedule(item) {
      const result = await this.$loaderWindow({
        title: `Create Schedule`,
        component: () => import("./EditTaskSchedule.vue"),
        width: "800px",
        showHeader: false,
        padding: false,
        closeOnClickModal: false,
        closeOnPressEscape: false,
        props: {
          value: item,
        },
      });
      this.$message.success("Schedule created successfully");
      if (result) this.tableCtrl.loadTableData();
    },
    handleApply() {
      this.filterPanelVisible = false;
      this.tableCtrl.loadTableData();
    },
  },
  components: {
    ETagFilterPanel,
  },
};
</script>

<style lang="scss" scoped>
.schedules-board {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;

  h2 {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
  }
}

.schedule-item {
  h3 {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
  }

  p {
    margin: 4px 0 8px;
    color: #4b5563;
  }

  .meta {
    display: flex;
    align-items: center;
    gap: 12px;
    font-size: 13px;
    color: #6b7280;
  }
}

.empty-state {
  margin-top: 24px;
  text-align: center;
  color: #6b7280;
}
</style>
